import { useCallback, useEffect, useMemo, useState } from 'react';
import { fieldErrorsOf, getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { rbacApi } from '../api/rbacApi';
import { EDITABLE_KEYS, IMPLIES_VIEW, PERMISSION_KEYS } from '../models/rbac.types';
import type {
  CheckState,
  MatrixGroup,
  MatrixItem,
  MatrixSaveResult,
  NewRolePayload,
  PermissionKey,
  PermissionMatrix,
  RbacRole,
} from '../models/rbac.types';

/**
 * The RBAC screen's state.
 *
 * Every edit is held here and written only on save, which is what makes Reset
 * possible and what the reference's action bar promises ("Changes apply after
 * saving"). The three derived checkbox states are computed from the booleans
 * rather than stored, so a badge and its boxes cannot disagree.
 */

/** Whether everything, nothing or some of a set of booleans is on. */
export function stateOf(flags: boolean[]): CheckState {
  if (flags.length === 0) return 'unchecked';
  if (flags.every(Boolean)) return 'checked';
  if (flags.every((flag) => !flag)) return 'unchecked';
  return 'indeterminate';
}

/** An item's five booleans, in column order. */
export function flagsOf(item: MatrixItem): boolean[] {
  return PERMISSION_KEYS.map((key) => item[key]);
}

/**
 * The booleans an administrator can change -- every column but READ, which is locked on.
 *
 * What the row ALL, the section ALL and the dot are drawn from: with READ always on, counting it would
 * leave a row that grants nothing else looking half-granted rather than empty.
 */
export function editableFlagsOf(item: MatrixItem): boolean[] {
  return EDITABLE_KEYS.map((key) => item[key]);
}

/**
 * An item with the matrix's two fixed rules applied: READ is on, and a row holding any of CREATE,
 * UPDATE or DELETE holds ACCESS.
 *
 * Applied to what is loaded as well as to every edit, so a matrix the platform answered before either
 * rule existed -- or one saved by an older client -- is drawn the way it will be stored.
 */
export function withImpliedView(item: MatrixItem): MatrixItem {
  /* A frozen item -- Organization Setup for every role but Mega Admin -- is kept exactly as stored. */
  if (item.frozen === true) return item;
  /* A locked item -- the Dashboard; RBAC for Admin and Mega Admin -- holds every action, whatever it was handed. */
  if (item.locked === true) {
    if (item.view && item.create && item.read && item.update && item.delete) return item;
    return { ...item, view: true, create: true, read: true, update: true, delete: true };
  }
  const implied = IMPLIES_VIEW.some((key) => item[key]);
  if (item.read && (!implied || item.view)) return item;
  return { ...item, read: true, view: item.view || implied };
}

/**
 * Sets one action on one row, keeping the rules: READ cannot be changed, ticking any of CREATE,
 * UPDATE or DELETE on a row ticks that row's ACCESS, and clearing ACCESS clears the three.
 */
export function withPermission(item: MatrixItem, key: PermissionKey, on: boolean): MatrixItem {
  if (key === 'read' || item.locked === true || item.frozen === true) return withImpliedView(item);
  if (key === 'view' && !on) {
    const cleared = { ...item, view: false };
    for (const implied of IMPLIES_VIEW) cleared[implied] = false;
    return withImpliedView(cleared);
  }
  return withImpliedView({ ...item, [key]: on });
}

/** Every editable column on or off together; READ stays on either way. */
function withAll(item: MatrixItem, on: boolean): MatrixItem {
  /* A locked row stays fully on, and a frozen one as stored, through Revoke All, a section's ALL and its own. */
  if (item.locked === true || item.frozen === true) return withImpliedView(item);
  return { ...item, view: on, create: on, read: true, update: on, delete: on };
}

/** Recomputed after every edit, so a group's badge always matches its rows. */
function rescore(group: MatrixGroup): MatrixGroup {
  const granted = group.items.reduce((sum, item) => sum + flagsOf(item).filter(Boolean).length, 0);
  return {
    ...group,
    granted,
    itemCount: group.items.length,
    total: group.items.length * PERMISSION_KEYS.length,
  };
}

/**
 * What a matrix grants, item by item -- every action of every drawn row, by feature -- so two matrices can be
 * compared for "anything changed" regardless of counts, labels or the order rows are drawn in.
 */
function grantsOf(matrix: PermissionMatrix | null): string {
  if (!matrix) return '';
  return matrix.groups
    .flatMap((group) => group.items)
    .map((item) =>
      [item.featureId, item.view, item.create, item.read, item.update, item.delete].map(String).join(':'),
    )
    .sort()
    .join('|');
}

/** The loaded matrix with the ACCESS rule applied and every count recomputed from it. */
export function normaliseMatrix(matrix: PermissionMatrix): PermissionMatrix {
  return rescoreMatrix({
    ...matrix,
    groups: matrix.groups.map((group) => ({
      ...group,
      items: group.items.map((item) => withImpliedView({ ...item, view: item.view === true })),
    })),
  });
}

function rescoreMatrix(matrix: PermissionMatrix): PermissionMatrix {
  const groups = matrix.groups.map(rescore);
  return {
    ...matrix,
    groups,
    granted: groups.reduce((sum, group) => sum + group.granted, 0),
    total: groups.reduce((sum, group) => sum + group.total, 0),
  };
}

export interface UseRbacMatrixResult {
  roles: RbacRole[];
  activeRoleId: number | null;
  matrix: PermissionMatrix | null;
  loading: boolean;
  saving: boolean;
  /** Whether any permission differs from what the role held when loaded or last saved. */
  dirty: boolean;
  error: string | null;
  /** The search across all three sections. */
  query: string;
  setQuery: (next: string) => void;
  /** Which sections are expanded, by group key. */
  open: Record<string, boolean>;
  toggleSection: (key: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  selectRole: (roleId: number) => void;
  /** The rows of a group after the search, which every bulk control acts on. */
  visibleItems: (group: MatrixGroup) => MatrixItem[];
  toggleCell: (groupKey: string, featureId: number, key: PermissionKey) => void;
  toggleRow: (groupKey: string, featureId: number) => void;
  toggleColumn: (groupKey: string, key: PermissionKey) => void;
  toggleGroup: (groupKey: string) => void;
  grantAll: () => void;
  revokeAll: () => void;
  reset: () => void;
  save: () => Promise<MatrixSaveResult | null>;
  createRole: (payload: NewRolePayload) => Promise<RbacRole | null>;
  /** The last refused create's messages by field (`name`, `description`), as the platform keys them. */
  createErrors: Record<string, string>;
  /** The last refused create's message, for the New Role dialog only -- never the page's banner behind it. */
  createError: string | null;
  deleteRole: (roleId: number) => Promise<boolean>;
  reload: () => void;
}

export function useRbacMatrix(): UseRbacMatrixResult {
  const [roles, setRoles] = useState<RbacRole[]>([]);
  const [activeRoleId, setActiveRoleId] = useState<number | null>(null);
  const [matrix, setMatrix] = useState<PermissionMatrix | null>(null);
  /* What the role held when it was loaded, so Reset has something to go back to. */
  const [pristine, setPristine] = useState<PermissionMatrix | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [createError, setCreateError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({
    FUNCTION: true,
    TEMPLATE: true,
    MASTER: true,
  });
  const [reloadToken, setReloadToken] = useState(0);

  /* The roles, and Admin as the one the screen opens on. */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const loaded = await rbacApi.listRoles();
        if (cancelled) return;

        setRoles(loaded);
        setActiveRoleId((current) => {
          if (current !== null) return current;
          /* Admin unless it is absent, which the reference's `activeRole` also does. */
          return loaded.find((role) => role.name === 'Admin')?.id ?? loaded[0]?.id ?? null;
        });
      } catch (err: unknown) {
        if (!cancelled) setError(getErrorMessage(normalizeError(err)));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  /* The active role's matrix. */
  useEffect(() => {
    if (activeRoleId === null) return undefined;

    let cancelled = false;
    setLoading(true);

    void (async () => {
      try {
        /* ACCESS rule applied on the way in, so Reset returns to what a save would store. */
        const loaded = normaliseMatrix(await rbacApi.readMatrix(activeRoleId));
        if (cancelled) return;

        setMatrix(loaded);
        setPristine(loaded);
        setError(null);
      } catch (err: unknown) {
        if (!cancelled) setError(getErrorMessage(normalizeError(err)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeRoleId, reloadToken]);

  const visibleItems = useCallback(
    (group: MatrixGroup): MatrixItem[] => {
      const term = query.trim().toLowerCase();
      if (term === '') return group.items;
      return group.items.filter((item) => item.name.toLowerCase().includes(term));
    },
    [query],
  );

  /** Applies a change to one group's items, leaving every other group alone. */
  const editGroup = useCallback(
    (groupKey: string, edit: (items: MatrixItem[], visible: Set<number>) => MatrixItem[]) => {
      setMatrix((current) => {
        if (!current) return current;

        const term = query.trim().toLowerCase();
        return rescoreMatrix({
          ...current,
          groups: current.groups.map((group) => {
            if (group.key !== groupKey) return group;

            /*
              The visible set, so a bulk control never changes a row the search is
              hiding -- the reference's own rule, and the one that matters most on
              Masters where eighteen rows do not fit at once.
            */
            const visible = new Set(
              group.items
                .filter((item) => term === '' || item.name.toLowerCase().includes(term))
                .map((item) => item.featureId),
            );

            return { ...group, items: edit(group.items, visible) };
          }),
        });
      });
    },
    [query],
  );

  const toggleCell = useCallback(
    (groupKey: string, featureId: number, key: PermissionKey) => {
      editGroup(groupKey, (items) =>
        items.map((item) =>
          item.featureId === featureId ? withPermission(item, key, !item[key]) : item,
        ),
      );
    },
    [editGroup],
  );

  const toggleRow = useCallback(
    (groupKey: string, featureId: number) => {
      editGroup(groupKey, (items) =>
        items.map((item) => {
          if (item.featureId !== featureId) return item;
          /* On unless it was already fully on, which is the reference's rule. */
          return withAll(item, !editableFlagsOf(item).every(Boolean));
        }),
      );
    },
    [editGroup],
  );

  const toggleColumn = useCallback(
    (groupKey: string, key: PermissionKey) => {
      editGroup(groupKey, (items, visible) => {
        /* A frozen row never changes, so it does not decide whether the toggle turns the rest on or off. */
        const scoped = items.filter((item) => visible.has(item.featureId) && item.frozen !== true);
        const on = !scoped.every((item) => item[key]);
        return items.map((item) =>
          visible.has(item.featureId) ? withPermission(item, key, on) : item,
        );
      });
    },
    [editGroup],
  );

  const toggleGroup = useCallback(
    (groupKey: string) => {
      editGroup(groupKey, (items, visible) => {
        /* A frozen row never changes, so it does not decide whether the toggle turns the rest on or off. */
        const scoped = items.filter((item) => visible.has(item.featureId) && item.frozen !== true);
        const on = !scoped.every((item) => editableFlagsOf(item).every(Boolean));
        return items.map((item) => (visible.has(item.featureId) ? withAll(item, on) : item));
      });
    },
    [editGroup],
  );

  const setEverything = useCallback((on: boolean) => {
    setMatrix((current) =>
      current
        ? rescoreMatrix({
            ...current,
            groups: current.groups.map((group) => ({
              ...group,
              items: group.items.map((item) => withAll(item, on)),
            })),
          })
        : current,
    );
  }, []);

  const save = useCallback(async (): Promise<MatrixSaveResult | null> => {
    if (!matrix || activeRoleId === null) return null;

    setSaving(true);
    try {
      const result = await rbacApi.saveMatrix(activeRoleId, matrix);
      setPristine(matrix);
      /* The rail's count follows the save without a second round trip. */
      setRoles((current) =>
        current.map((role) =>
          role.id === activeRoleId ? { ...role, grantedCount: result.granted } : role,
        ),
      );
      setError(null);
      return result;
    } catch (err: unknown) {
      setError(getErrorMessage(normalizeError(err)));
      return null;
    } finally {
      setSaving(false);
    }
  }, [activeRoleId, matrix]);

  const createRole = useCallback(async (payload: NewRolePayload): Promise<RbacRole | null> => {
    setCreateErrors({});
    setCreateError(null);
    try {
      const created = await rbacApi.createRole(payload);
      /* A new role holds READ on every item; the platform answers the count it created it with. */
      setRoles((current) => [...current, { ...created, grantedCount: created.grantedCount ?? 0 }]);
      setActiveRoleId(created.id);
      setError(null);
      return created;
    } catch (err: unknown) {
      const apiError = normalizeError(err);
      /*
        Said in the New Role dialog, under the field it is about -- not on the page, whose banner sits behind the
        dialog's mask and would only repeat "Request validation failed".
      */
      setCreateError(getErrorMessage(apiError));
      setCreateErrors(fieldErrorsOf(apiError));
      return null;
    }
  }, []);

  const deleteRole = useCallback(
    async (roleId: number): Promise<boolean> => {
      try {
        await rbacApi.deleteRole(roleId);
        setRoles((current) => current.filter((role) => role.id !== roleId));
        setActiveRoleId((current) => {
          if (current !== roleId) return current;
          return roles.find((role) => role.id !== roleId)?.id ?? null;
        });
        setError(null);
        return true;
      } catch (err: unknown) {
        setError(getErrorMessage(normalizeError(err)));
        return false;
      }
    },
    [roles],
  );

  /* Whether any permission differs from what the role held when loaded or last saved: Save Changes needs one. */
  const dirty = useMemo(() => matrix !== null && grantsOf(matrix) !== grantsOf(pristine), [matrix, pristine]);

  const activeRole = useMemo(
    () => roles.find((role) => role.id === activeRoleId) ?? null,
    [activeRoleId, roles],
  );

  return {
    roles,
    activeRoleId,
    matrix: matrix
      ? { ...matrix, roleName: activeRole?.name ?? matrix.roleName }
      : null,
    loading,
    saving,
    dirty,
    error,
    query,
    setQuery,
    open,
    toggleSection: (key) => {
      setOpen((current) => ({ ...current, [key]: !current[key] }));
    },
    expandAll: () => {
      setOpen({ FUNCTION: true, TEMPLATE: true, MASTER: true });
    },
    collapseAll: () => {
      setOpen({ FUNCTION: false, TEMPLATE: false, MASTER: false });
    },
    selectRole: setActiveRoleId,
    visibleItems,
    toggleCell,
    toggleRow,
    toggleColumn,
    toggleGroup,
    grantAll: () => {
      setEverything(true);
    },
    revokeAll: () => {
      setEverything(false);
    },
    reset: () => {
      setMatrix(pristine);
      setQuery('');
    },
    save,
    createRole,
    createErrors,
    createError,
    deleteRole,
    reload: () => {
      setReloadToken((token) => token + 1);
    },
  };
}
